import { WintSuperkollAPIModelTimeReporting as ApiModel } from "wint-api-client/dist/types";

export interface MonthTotalRowKey {
  projectId: string;
  categoryId: number;
}

export enum MonthTotalIssue {
  BelowFixedHours = "BelowFixedHours",
  NoOpenWorkDays = "NoOpenWorkDays",
  ExceedsDayLimit = "ExceedsDayLimit",
}

// The weekly view caps a project cell at the same value; the server has no cap of its own.
export const MaxHoursPerDay = 24;

// Time reporting expects 8 h per workday, so a day with that much absence has no room left for project time.
const FullDayAbsenceHours = 8;

export interface MonthTotalPlan {
  /** Hours the total cannot move: locked days, invoiced time, weekends, holidays, full days of absence, and days outside the project or category period. */
  fixedHours: number;
  /** Hours already reported on the days the save rewrites. */
  replaceableHours: number;
  workDayCount: number;
  /** The highest total the row can take without going over the daily limit. */
  maxTotal: number;
  /** The row's hours per in-month day after the save, keyed by `toDateKey`. */
  dayHours: Record<string, number>;
  issue?: MonthTotalIssue;
}

// Hours are stored with two decimals, so all arithmetic runs in whole hundredths to avoid float drift.
export const toCents = (hours: number) => Math.round(hours * 100);

const startOfDay = (date: Date | string) => {
  const day = new Date(date);
  day.setHours(0, 0, 0, 0);
  return day;
};

export const toDateKey = (date: Date | string) => {
  const day = new Date(date);
  return `${day.getFullYear()}-${day.getMonth() + 1}-${day.getDate()}`;
};

const isWithin = (date: Date, start?: Date | string, end?: Date | string) =>
  (!start || startOfDay(start) <= date) && (!end || date <= startOfDay(end));

export const periodOverlapsMonth = (monthStart: Date, monthEnd: Date, start?: Date | string, end?: Date | string) =>
  (!start || startOfDay(start) <= startOfDay(monthEnd)) && (!end || startOfDay(monthStart) <= startOfDay(end));

const isDayOpen = (day: ApiModel.TimeReport) =>
  !day.isLocked && day.timeReportStatus === ApiModel.TimeReportStatus.Open;

const isFullDayAbsence = (day: ApiModel.TimeReport) =>
  (day.deviations ?? []).reduce((cents, deviation) => cents + toCents(deviation.hours), 0) >= FullDayAbsenceHours * 100;

const findCategoryTime = (day: ApiModel.TimeReport, key: MonthTotalRowKey) =>
  day.projectTimes
    ?.find((pt) => pt.projectId === key.projectId)
    ?.categoryTimes?.find((ct) => ct.categoryId === key.categoryId);

export const getMonthDays = (metaData: ApiModel.TimeReportMetaDataWithPeriods, monthStart: Date, monthEnd: Date) => {
  const start = startOfDay(monthStart);
  const end = startOfDay(monthEnd);
  return (metaData.weeks ?? [])
    .flatMap((week) => week.timeReports ?? [])
    .filter((day) => {
      const date = startOfDay(day.timeLogDate);
      return start <= date && date <= end;
    });
};

// Days get whole quarter hours so the report reads like hand-entered time rather than 1.82 / 1.81 h.
const QuarterHourCents = 25;

export const spreadEvenly = (hours: number, dayCount: number) => {
  const cents = toCents(hours);
  if (dayCount <= 0) {
    return [];
  }
  const base = Math.floor(cents / QuarterHourCents / dayCount) * QuarterHourCents;
  const leftover = cents - base * dayCount;
  // Fewer leftover quarters than days, so each goes to a different day, spaced through the month: front-loading
  // them would make the first weeks heavier, and with several rows planned alike that adds up to a visible gap in
  // the weekly comp time. Minutes that do not make a whole quarter go to the first day without an extra quarter,
  // which keeps every day within the daily limit.
  const extraQuarters = Math.floor(leftover / QuarterHourCents);
  const oddCents = leftover - extraQuarters * QuarterHourCents;
  const getsExtraQuarter = (index: number) =>
    Math.floor(((index + 1) * extraQuarters) / dayCount) > Math.floor((index * extraQuarters) / dayCount);
  const oddCentsDay = Array.from({ length: dayCount }, (_, index) => index).find((index) => !getsExtraQuarter(index));
  return Array.from({ length: dayCount }, (_, index) => {
    const extra = getsExtraQuarter(index) ? QuarterHourCents : index === oddCentsDay ? oddCents : 0;
    return (base + extra) / 100;
  });
};

export const planMonthTotal = (
  metaData: ApiModel.TimeReportMetaDataWithPeriods,
  monthDays: ApiModel.TimeReport[],
  key: MonthTotalRowKey,
  total: number
): MonthTotalPlan => {
  const project = metaData.projects?.find((p) => p.id === key.projectId);
  const category = project?.categories?.find((c) => c.id === key.categoryId);

  let fixedCents = 0;
  let replaceableCents = 0;
  const dayHours: Record<string, number> = {};
  const workDays: string[] = [];

  monthDays.forEach((day) => {
    const date = startOfDay(day.timeLogDate);
    const dateKey = toDateKey(date);
    const categoryTime = findCategoryTime(day, key);
    const hours = categoryTime?.hours ?? 0;
    // The save validates removed time too, so time outside the project or category period is left alone rather than
    // cleared.
    const isActive =
      !!category &&
      isWithin(date, project?.startDate, project?.endDate) &&
      isWithin(date, category.startDate, category.endDate);

    // Weekend and holiday time can carry salary meaning (overtime, inconvenient hours), so it stays on its day.
    if (!isDayOpen(day) || categoryTime?.isLocked || !isActive || !day.isWorkDay || isFullDayAbsence(day)) {
      fixedCents += toCents(hours);
      dayHours[dateKey] = hours;
      return;
    }

    replaceableCents += toCents(hours);
    dayHours[dateKey] = 0;
    workDays.push(dateKey);
  });

  const spreadCents = toCents(total) - fixedCents;
  const maxCents = fixedCents + workDays.length * MaxHoursPerDay * 100;
  let issue: MonthTotalIssue | undefined;
  if (spreadCents < 0) {
    issue = MonthTotalIssue.BelowFixedHours;
  } else if (spreadCents > 0 && workDays.length === 0) {
    issue = MonthTotalIssue.NoOpenWorkDays;
  } else if (toCents(total) > maxCents) {
    issue = MonthTotalIssue.ExceedsDayLimit;
  } else {
    spreadEvenly(spreadCents / 100, workDays.length).forEach((hours, index) => {
      dayHours[workDays[index]] = hours;
    });
  }

  return {
    fixedHours: fixedCents / 100,
    replaceableHours: replaceableCents / 100,
    workDayCount: workDays.length,
    maxTotal: maxCents / 100,
    dayHours,
    issue,
  };
};

/** Sums a plan's day hours into the month view's week columns, keyed like "1-6" (first and last day of month). */
export const getWeekColumnHours = (dayHours: Record<string, number>, weekColumns: string[]) => {
  const result: Record<string, number> = {};
  weekColumns.forEach((column) => {
    const [firstDay, lastDay] = column.split("-").map(Number);
    let cents = 0;
    Object.entries(dayHours).forEach(([dateKey, hours]) => {
      const dayOfMonth = Number(dateKey.split("-")[2]);
      if (firstDay <= dayOfMonth && dayOfMonth <= lastDay) {
        cents += toCents(hours);
      }
    });
    result[column] = cents / 100;
  });
  return result;
};

// SetProjectTimes makes each posted row's days between From and To exactly the days posted: a day left out loses
// its time, and nothing outside the posted rows changes. So every day the row keeps hours on goes in, the days the
// total cannot move included, at the hours they already have.
export const buildMonthTotalsProjectTimesPost = (
  metaData: ApiModel.TimeReportMetaDataWithPeriods,
  monthStart: Date,
  monthEnd: Date,
  plans: { key: MonthTotalRowKey; plan: MonthTotalPlan }[]
): ApiModel.ProjectTimesPost => {
  const monthDays = getMonthDays(metaData, monthStart, monthEnd);
  return {
    employeeId: metaData.employeeId,
    // Local midnights: the app's Date.toJSON writes local time, so the server reads the same calendar days.
    from: startOfDay(monthStart),
    to: startOfDay(monthEnd),
    rows: plans.map(({ key, plan }) => ({
      projectId: key.projectId,
      categoryId: key.categoryId,
      days: monthDays
        .map((day) => ({
          date: startOfDay(day.timeLogDate),
          hours: plan.dayHours[toDateKey(day.timeLogDate)] ?? findCategoryTime(day, key)?.hours ?? 0,
        }))
        .filter((day) => day.hours > 0),
    })),
  };
};
